// src/resources/dashboards/Painel360/LoyaltySection.tsx
import React from 'react';
import { Box, Button, Chip, CircularProgress, Tooltip, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FavoriteIcon from '@mui/icons-material/Favorite';
import dayjs from 'dayjs';
import { motion } from 'framer-motion';
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import ManualLinkButton from '../../../manual/components/ManualLinkButton';
import { __t } from '../../../i18n/t';
import {
  DEFAULT_RFM_CUTOFFS, RFM_MANUAL_SECTION, RFM_SEGMENT_ORDER, SEGMENT_META,
  deltaColor, segmentCriteria, segmentDelta,
} from '../../customerRfm/segments';
import { COLORS, EmptyHint, ErrorCard, SectionHeader, SkeletonCard, cardSx, fmtBrl, fmtNum, openInNewTab } from './shared';
import { LoyaltyData, SectionState } from './types';

const LIFECYCLE_META = [
  { key: 'active', label: 'Ativos', color: COLORS.success },
  { key: 'atRisk', label: 'Em risco', color: COLORS.warning },
  { key: 'inactive', label: 'Inativos', color: COLORS.danger },
] as const;

// `action` opcional: mesmo padrão do Card de EngagementSection.tsx — botão solto
// na linha do título (usado aqui pelo "Ver detalhes" do card "Ciclo de vida da base").
// `asOf`: selo com a data a que o card se refere. Sem ele, quem filtrava agosto via a base de hoje
// achando que era agosto.
const Card = ({ title, hint, action, asOf, children }: { title: string; hint: string; action?: React.ReactNode; asOf?: React.ReactNode; children: React.ReactNode }) => (
  <Box sx={cardSx}>
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: COLORS.ink }}>{title}</Typography>
        {asOf}
      </Box>
      {action}
    </Box>
    <Typography sx={{ fontSize: 12, color: COLORS.muted, mb: 1.5 }}>{hint}</Typography>
    {children}
  </Box>
);

// Textos já passam por __t na chamada (a prop não é literal, o plugin de i18n não a embrulha).
const AsOfChip = ({ label, tooltip }: { label: string; tooltip: string }) => (
  <Tooltip title={tooltip} arrow>
    <Chip size="small" label={label}
      sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: 'rgba(120,105,223,0.1)', color: COLORS.brand }} />
  </Tooltip>
);

// Placeholder enquanto o rollup pré-agregado de fidelidade é construído na 1ª carga da rede.
const ComputingHint = ({ height = 160 }: { height?: number }) => (
  <Box sx={{ height, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.25, textAlign: 'center' }}>
    <CircularProgress size={18} sx={{ color: COLORS.brand }} />
    <Typography variant="body2" sx={{ color: COLORS.muted, maxWidth: 280 }}>
      Preparando os dados de fidelidade…
    </Typography>
    <Typography sx={{ fontSize: 11.5, color: COLORS.faint, maxWidth: 280 }}>
      A primeira carga da rede leva alguns minutos.
    </Typography>
  </Box>
);

const LoyaltySection = ({ state, canCreateRecovery, stationIds }: {
  state: SectionState<LoyaltyData>; canCreateRecovery?: boolean;
  // Posto(s) filtrados no topo do Painel 360 — repassado ao "Ver detalhes" do card RFM
  // para a tela abrir já com o mesmo recorte.
  stationIds?: string[];
}) => {
  if (state.loading) return <SkeletonCard height={420} />;
  if (state.error) return <ErrorCard onRetry={state.refetch} height={420} />;
  if (!state.data) return null;
  const { lifecycle, baseTotal, cohorts, cohortsUntil, rfm, rfmMeta, revenueAtRisk, avgIntervalDays, computing } = state.data;
  const todayChip = <AsOfChip label={__t('Retrato de hoje')} tooltip={__t('Não muda com o período escolhido no topo da página.')} />;
  const lifecycleChip = lifecycle.asOf ? (
    <AsOfChip
      label={__t('Posição em %{date}', { date: dayjs(`${lifecycle.asOf}-01`).endOf('month').format('DD/MM/YYYY') })}
      tooltip={__t('Como a base estava no fim do mês em que o período termina, com os prazos de inatividade de hoje.')} />
  ) : (
    <AsOfChip label={__t('Retrato de hoje')}
      tooltip={__t('Base de hoje. Se o período terminar num mês já fechado (até 12 meses atrás), o card mostra como ela estava no fim daquele mês.')} />
  );
  const cohortsChip = cohortsUntil ? (
    <AsOfChip
      label={__t('Até %{month}', { month: dayjs(`${cohortsUntil}-01`).format('MMM/YY') })}
      tooltip={__t('Safras e retornos até o mês em que o período termina. O mês em andamento só entra quando fechar.')} />
  ) : undefined;

  const donutData = LIFECYCLE_META.map(m => ({ name: m.label, value: lifecycle[m.key], color: m.color }));
  const rfmBydSegment = new Map(rfm.map(r => [r.segment, r]));
  const maxRfmCustomers = Math.max(1, ...rfm.map(r => r.customers));
  const rfmTotalCustomers = rfm.reduce((sum, r) => sum + r.customers, 0);
  const rfmTotalValue = rfm.reduce((sum, r) => sum + r.totalValue, 0);
  const rfmCutoffs = rfmMeta?.cutoffs ?? DEFAULT_RFM_CUTOFFS;
  const goToCustomerRfm = (params?: Record<string, string>) => {
    const search = new URLSearchParams(params);
    if (stationIds?.length) search.set('mainPlaceIds', JSON.stringify(stationIds));
    const qs = search.toString();
    // Nova aba, como os outros atalhos do Painel 360 (o painel fica aberto com os filtros).
    openInNewTab(`/customer-rfm${qs ? `?${qs}` : ''}`);
  };

  return (
    <Box>
      <SectionHeader
        icon={<FavoriteIcon sx={{ fontSize: 18 }} />}
        gradient={`linear-gradient(135deg, ${COLORS.brand}, ${COLORS.purple})`}
        title="Fidelidade — seus clientes estão voltando?"
        subtitle="Ciclo de vida, retenção por safra e segmentos de valor"
      />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 1.75 }}>

        <Card title="Ciclo de vida da base"
          asOf={computing ? undefined : lifecycleChip}
          hint={`Critério de inatividade: ${lifecycle.thresholds.atRiskDays} dias (risco) / ${lifecycle.thresholds.inactiveDays} dias (inativo)`}
          action={(
            <Button
              size="small"
              variant="outlined"
              onClick={() => openInNewTab(`/customer-lifecycle?atRiskDays=${lifecycle.thresholds.atRiskDays}&inactiveDays=${lifecycle.thresholds.inactiveDays}`)}
              endIcon={<ArrowForwardIcon fontSize="small" />}
              sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              Ver detalhes
            </Button>
          )}
        >
          {computing ? <ComputingHint /> : lifecycle.total === 0 ? <EmptyHint text="Ainda não há clientes suficientes." /> : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
              <Box sx={{ width: 180, height: 180, position: 'relative' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={donutData} dataKey="value" innerRadius={58} outerRadius={82} paddingAngle={2} isAnimationActive>
                      {donutData.map((d, i) => <Cell key={i} fill={d.color} />)}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                  <Typography sx={{ fontSize: 24, fontWeight: 800, color: COLORS.ink }}>{fmtNum(lifecycle.total)}</Typography>
                  <Typography sx={{ fontSize: 11, color: COLORS.muted }}>clientes na base</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1, minWidth: 220 }}>
                {LIFECYCLE_META.map(m => (
                  <Box key={m.key} sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 13 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: m.color }} />
                    <Typography sx={{ fontSize: 13, color: COLORS.ink }}>{m.label}</Typography>
                    <Typography sx={{ ml: 'auto', fontWeight: 800, fontSize: 13 }}>
                      {fmtNum(lifecycle[m.key])} · {lifecycle.total ? Math.round((lifecycle[m.key] / lifecycle.total) * 100) : 0}%
                    </Typography>
                  </Box>
                ))}
                <Chip size="small" sx={{ alignSelf: 'flex-start', mt: 0.5, fontWeight: 700, bgcolor: 'rgba(59,130,246,0.1)', color: COLORS.blue }}
                  label={`${fmtNum(lifecycle.recovered)} clientes recuperados no período`} />
                {avgIntervalDays.value !== null && (
                  <Typography sx={{ fontSize: 12, color: COLORS.muted, mt: 0.5 }}>
                    Frequência média: <b style={{ color: COLORS.ink }}>1 abastecimento a cada {fmtNum(avgIntervalDays.value, 1)} dias</b>
                    {avgIntervalDays.previous !== null && ` (era ${fmtNum(avgIntervalDays.previous, 1)} no período anterior)`}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Card>

        <Card title="Retenção por safra de clientes" hint="% de clientes de cada mês de primeiro abastecimento que continuam voltando"
          asOf={computing ? undefined : cohortsChip}>
          {computing ? <ComputingHint /> : !cohorts.length ? <EmptyHint text="Ainda não há histórico suficiente para cohorts." /> : (
            <Box sx={{ display: 'grid', gridTemplateColumns: '64px repeat(12, 1fr)', gap: 0.5, fontSize: 10, color: COLORS.faint }}>
              <Box />
              {Array.from({ length: 12 }, (_, i) => (
                <Box key={i} sx={{ textAlign: 'center', fontSize: 10 }}>M{i + 1}</Box>
              ))}
              {cohorts.map((c, r) => (
                <React.Fragment key={c.cohortMonth}>
                  <Box sx={{ fontSize: 11, fontWeight: 700, color: COLORS.muted, display: 'flex', alignItems: 'center' }}>
                    {dayjs(c.cohortMonth + '-01').format('MMM/YY')}
                  </Box>
                  {c.retention.map((pct, i) => (
                    <motion.div key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: (r * 12 + i) * 0.004 }}>
                      <Box title={`${pct}%`} sx={{
                        aspectRatio: '1.5', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 9.5, fontWeight: 700, color: pct > 0 ? '#fff' : 'transparent',
                        bgcolor: pct > 0 ? `rgba(120,105,223,${(0.15 + (Math.min(pct, 100) / 100) * 0.85).toFixed(2)})` : '#f8fafc',
                      }}>{pct > 0 ? `${pct}%` : ''}</Box>
                    </motion.div>
                  ))}
                </React.Fragment>
              ))}
            </Box>
          )}
        </Card>

        <Card
          title="Segmentos de valor (RFM)"
          asOf={computing ? undefined : todayChip}
          hint={rfmMeta ? 'Recência e frequência dos últimos 12 meses · régua fixa' : 'Recência × frequência × valor dos últimos 12 meses'}
          action={(
            <Button
              size="small"
              variant="outlined"
              onClick={() => goToCustomerRfm()}
              endIcon={<ArrowForwardIcon fontSize="small" />}
              sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              Ver detalhes
            </Button>
          )}
        >
          {computing ? <ComputingHint /> : !rfm.length ? <EmptyHint text="Sem dados de segmentação ainda." /> : (
            <>
              {RFM_SEGMENT_ORDER.map(seg => {
                const row = rfmBydSegment.get(seg);
                const meta = SEGMENT_META[seg];
                const width = row ? Math.max(8, (row.customers / maxRfmCustomers) * 100) : 0;
                const pctRevenue = row && rfmTotalValue ? row.totalValue / rfmTotalValue : null;
                const delta = rfmMeta ? segmentDelta(row) : null;
                const clickable = !!rfmMeta;
                return (
                  <Box
                    key={seg}
                    onClick={clickable ? () => goToCustomerRfm({ segment: seg }) : undefined}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.25,
                      cursor: clickable ? 'pointer' : 'default', borderRadius: 1.5, px: 0.5, py: 0.25, mx: -0.5,
                      '&:hover': clickable ? { bgcolor: 'rgba(120,105,223,0.05)' } : undefined,
                    }}
                  >
                    <Tooltip title={segmentCriteria(seg, rfmCutoffs)} arrow>
                      {/* i18n: meta.label é valor cru (não passa pelo tradutor sozinho) — __t
                          explícito, mesma tradução já usada na tela /customer-rfm. */}
                      <Typography sx={{ width: 110, fontSize: 12.5, fontWeight: 700, color: COLORS.ink, flexShrink: 0 }}>{meta.emoji} {__t(meta.label)}</Typography>
                    </Tooltip>
                    <Box sx={{ width: 90, height: 14, bgcolor: '#f1f5f9', borderRadius: 1, overflow: 'hidden', flexShrink: 0 }}>
                      <motion.div initial={{ width: 0 }} animate={{ width: `${width}%` }} transition={{ duration: 0.9 }}
                        style={{ height: '100%', borderRadius: 6, background: meta.color }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      {/* Valor SEMPRE fora da barra, escuro — sempre legível (barra estreita não corta o texto branco) */}
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: COLORS.ink, whiteSpace: 'nowrap' }}>
                        {row ? <>{fmtNum(row.customers)} <Typography component="span" sx={{ color: COLORS.muted, fontWeight: 600 }}>· {fmtBrl(row.totalValue)}</Typography></> : '0'}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        {pctRevenue !== null && (
                          <Typography sx={{ fontSize: 11, color: COLORS.muted, whiteSpace: 'nowrap' }}>
                            {(pctRevenue * 100).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}% do faturamento
                          </Typography>
                        )}
                        {delta !== null && (
                          <Tooltip title="Variação nos últimos 30 dias" arrow>
                            <Box component="span" sx={{
                              display: 'inline-flex', alignItems: 'center', gap: 0.4, fontSize: 10.5, fontWeight: 700,
                              borderRadius: 999, px: 0.75, py: 0.1, color: deltaColor(seg, delta),
                              bgcolor: delta === 0 ? 'rgba(100,116,139,0.08)' : `${deltaColor(seg, delta)}1a`,
                            }}>
                              {delta >= 0 ? '▲' : '▼'} {fmtNum(Math.abs(delta))}
                            </Box>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                  </Box>
                );
              })}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 0.5 }}>
                <Typography sx={{ fontSize: 12, color: COLORS.faint }}>
                  {fmtNum(rfmTotalCustomers)} de {fmtNum(baseTotal ?? lifecycle.total)} clientes da base · quem não abasteceu em 12 meses fica de fora
                </Typography>
                <ManualLinkButton slug="dashboard-v2" section={RFM_MANUAL_SECTION} label="Como funciona?" compact />
              </Box>
            </>
          )}
        </Card>

        <Card title="Receita em risco" hint="Clientes valiosos que pararam de abastecer — dá pra trazer de volta"
          asOf={computing ? undefined : todayChip}>
          {(computing || !revenueAtRisk) ? <ComputingHint /> : revenueAtRisk.atRiskCustomers === 0 ? (
            <EmptyHint text="Nenhum cliente valioso esfriou. 👏 Sua base de maior valor segue ativa." />
          ) : (
            <Box>
              <Typography sx={{ fontSize: 32, fontWeight: 800, color: COLORS.danger, lineHeight: 1.1 }}>
                {fmtBrl(revenueAtRisk.atRiskMonthlyRevenue)}
                <Typography component="span" sx={{ fontSize: 13, color: COLORS.muted, fontWeight: 600, ml: 0.75 }}>/mês em jogo</Typography>
              </Typography>
              <Typography sx={{ fontSize: 13.5, color: COLORS.ink, mt: 0.75 }}>
                <b>{fmtNum(revenueAtRisk.atRiskCustomers)}</b> clientes de alto valor pararam de voltar — ainda recuperáveis
              </Typography>
              {revenueAtRisk.inactiveCustomers > 0 && (
                <Typography sx={{ fontSize: 12, color: COLORS.faint, mt: 0.75 }}>
                  + {fmtNum(revenueAtRisk.inactiveCustomers)} já inativos há mais tempo (mais difíceis de reverter)
                </Typography>
              )}
              {canCreateRecovery && (
                <Button size="small" onClick={() => openInNewTab('/customer-lifecycle')}
                  sx={{ mt: 2, fontWeight: 700, fontSize: 12.5, color: '#fff', px: 1.75,
                    background: `linear-gradient(135deg, ${COLORS.brand2}, ${COLORS.brand})`,
                    '&:hover': { background: `linear-gradient(135deg, ${COLORS.brand}, ${COLORS.brand2})` } }}>
                  Criar regra de recuperação →
                </Button>
              )}
            </Box>
          )}
        </Card>
      </Box>
    </Box>
  );
};

export default LoyaltySection;
